// Depression: an army that cannot win even against an opponent that has
// stopped playing.
//
// This is stalemate handling for a game that is not really about stalemate. A
// side reduced to a king and nothing else is not *stuck* in the chess sense —
// it has legal moves, it is not in check, no draw rule has fired — it simply
// has no path to a win and will shuffle until the fifty-move rule or the ply
// cap. That is a quarter of an hour of a game that finished some time ago.
//
// **The test is a simulation, and it has to be.** The obvious cheap proxy is
// the insufficient-material rule: king alone, king and bishop, king and knight
// cannot mate. In *this* game that proxy is wrong, and interestingly so — a
// lone king standing beside a graveyard is a factory. It digs, it refines, it
// forges a rook, and then it can mate. So "can this army win" is a question
// about mining and cooking and building as much as about pieces, and the only
// honest way to ask it is to play it out.
//
// **Why it lives here and not in `src/engine/rules/`.** The rules core depends on
// nothing and may not consult the AI; "could this be won" is a question about
// how well the game can be played. So the verdict is produced here and crosses
// into the rules as an ordinary `set-despair` command — the same route every
// other thing the opponent knows takes. The rules hold the *stamp* and the
// grace-period reprieve; they do not hold the reasoning.

import { PAWN, pieceColor, pieceType, type Color, type Side } from "src/engine/rules/board";
import { applyCommand, type GameState } from "src/engine/rules/commands";
import type { FloorCommand } from "src/engine/rules/turns";
import { type GameStatus, isGameOver } from "src/engine/rules/chess/status";
import { legalMoves } from "src/engine/rules/chess/moveGen";
import {
  areEnemies,
  DESPAIR_IMMUNE,
  DESPAIR_NONE,
  livingSides
} from "src/engine/rules/position";
import { holdsGround } from "src/engine/rules/industry/belts";
import { tallySide } from "src/engine/rules/tally";
import {
  beltOwnerAt,
  cargoTotal,
  droppedAt,
  mineableAt,
  mineableSquares,
  liveSquares,
  structureAt,
  structureSquares,
  unitCount
} from "src/engine/rules/industry/state";
import { RULES } from "src/engine/rules/rules";
import { despairTurnsFor } from "src/engine/rules/campaign/mode";
import { despairSlot, graceShape, ownTurnsSince } from "src/engine/rules/campaign/despair";
import { type Level, skillFor } from "./difficulty";
import { planOnce } from "./opponent";

/**
 * Is this army in a position it cannot win from, given every turn it wants?
 *
 * Two questions, and the cheap one comes first on purpose.
 *
 * **Is it destitute at all?** A fact about the board: what it holds, and what
 * it has left to hold it with. Nothing below this bar is even considered.
 *
 * **And could it still win?** "Given every turn it wants" is the trick: the
 * opponents are **frozen**, so what is measured is the army's own ceiling
 * rather than the fight. An army that cannot beat a statue in sixty-four turns
 * is not going to beat a player.
 *
 * The order is load-bearing and it is the fix for this mechanic's worst bug.
 * The simulation used to be the *only* test, and a simulation is a judgement
 * about how well a position can be played — so a lone king beside forty scrap
 * was condemned because the cheap planner never stepped onto the heap, not
 * because the position was hopeless. That is the exact case this file's own
 * header says must survive. A fact now gates the judgement; the judgement no
 * longer gets to overrule the fact.
 *
 * **Cheap first, and measured**: the gate costs 0.03–0.13ms and the rollout
 * costs 44ms on a small board — far more on a grown one, since it plans a
 * turn and ticks every factory sixty-four times. This runs once a turn for
 * every living side, so an army that is merely losing now pays a hundredth of
 * a millisecond to be left alone. `fightingPieces` is asked before
 * `workableMaterial` for the same reason: it walks the board, where the other
 * walks the board *and* every industry map, and any army with two real pieces
 * is answered without touching them.
 *
 * Returns false — never depressed — when there is nobody to be depressed
 * *about*. A side alone on the board has won or is between waves, and calling
 * that despair would surrender armies at every stage boundary.
 */
export function isDepressed(
  state: GameState,
  side: Side,
  level: Level = "normal",
  horizon: number = RULES.despairHorizon
): boolean {
  if (!hasOpponent(state, side)) return false;
  if (!destitute(state, side)) return false;
  return !canWinAlone(state, side, level, horizon);
}

/** Somebody on the board who is neither us nor an ally. */
function hasOpponent(state: GameState, side: Side): boolean {
  return livingSides(state.position).some((other) =>
    areEnemies(state.position, side, other)
  );
}

/**
 * The turn's despair decision, as the commands that carry it out.
 *
 * Extracted from `useSoloOpponent`'s effect for `runStep`'s reason: a decision
 * that lives only inside a React effect can never be exercised headless, and
 * from §26 F9 this one decides whether an *enemy camp* lays down its arms — a
 * thing `playRun` has to model or the level checker grades a game nobody
 * plays. The driver keeps the timing (once per turn, at its start — never
 * mid-turn, §31 T) and the dispatching; this is the whole of what it decides.
 *
 * **Every living side, enemies included** (§26 F9). It used to be the
 * player's army alone in a run (`runSidesOnly`), on the argument that a camp
 * giving up hands the front over for free on a fact about its planner. The
 * grace period reverses that on purpose: an army reduced to a lone Monarch
 * with nothing to build is given `despairTurnsFor(run)` to put another piece on
 * the board, whoever it belongs to, and a camp that cannot is a fight that is
 * already over — the reducer's `surrender` turns its front over as a beaten
 * army's mate would. `destitute`'s gate is what keeps this honest for a camp
 * that is merely behind.
 *
 * `seen` is the caller's memory of which sides have been judged at least
 * once, mutated here: an army that is depressed **the first time it is ever
 * looked at** spawned that way — a camp of three pawns and a king is a short
 * fight, not a coma — and is exempted for good. Never the player's side
 * (`tallySide`), so a continued run — empty memory — cannot exempt it.
 *
 * Only commands that change something are emitted (the reducer is idempotent
 * about the rest), so a quiet board answers an empty list and no log fills up
 * with no-ops.
 */
export function despairStep(
  state: GameState,
  level: Level,
  seen: Set<number>
): FloorCommand[] {
  // `FloorCommand`, not `Command` (§29 U2): the director issues these on
  // whoever's clock it is, so Undo must not offer a diagnosis or a surrender
  // as the player's last action.
  const out: FloorCommand[] = [];
  for (const side of livingSides(state.position)) {
    const stamp = state.position.despair[side] ?? DESPAIR_NONE;
    // Exempt for good — it arrived unable to win. Asked before the check,
    // because the check is a sixty-four-turn rollout and there is no answer
    // it could give that would change anything.
    if (stamp === DESPAIR_IMMUNE) continue;

    // **The grace period's shape is a fact, not a judgement** (§31 K6): only
    // Monarchs left, or none — one own turn to forge, whatever a rollout says.
    const sad =
      (hasOpponent(state, side) && graceShape(state.position, side)) ||
      isDepressed(state, side, level);

    if (sad && side !== tallySide(state.run) && !seen.has(side)) {
      out.push({ type: "set-despair", side, depressed: false, immune: true });
      seen.add(side);
      continue;
    }
    seen.add(side);
    if (sad !== stamp >= 0) {
      out.push({ type: "set-despair", side, depressed: sad });
    }
    if (!sad) continue;
    // **The stamp this turn ends with, not the one it began with.** A side
    // first diagnosed here has no stamp yet — the `set-despair` above lands
    // it, at this turn's slot — so reading the stored one would make the grace
    // period a turn longer and a period of *nought* (Hard Mode, §26 F13)
    // unreachable. A fresh stamp has served no turns, so a grace of one is
    // still served in full.
    const landed = stamp >= 0 ? stamp : despairSlot(state.position);
    // The side's own completed turns since the stamp (§31 T), through the same
    // helper the reducer's gate and the banner read.
    if (ownTurnsSince(state.position, side, landed) >= despairTurnsFor(state.run)) {
      out.push({ type: "surrender", side });
    }
  }
  return out;
}

/**
 * Down to a king and a few scraps: the only state despair may be asked about.
 *
 * Both conditions, because either alone is wrong. Material without pieces is a
 * factory that has not built anything yet, which is a *good* position in this
 * game and the whole reason the test is not the insufficient-material rule.
 * Pieces without material is an army that can still fight and still capture,
 * and every capture leaves scrap — so the metal is one exchange away.
 */
export function destitute(state: GameState, side: Side): boolean {
  // **Short-circuited, and it matters more than it used to.** Counting
  // material now asks `holdsGround` per heap, which walks every piece on the
  // board — so it is the expensive half twice over, and any army with two real
  // pieces is answered without paying for it. Do not collapse this into
  // `holdings`, which computes both because a banner needs both.
  return (
    fightingPieces(state, side) < RULES.despairPieces &&
    workableMaterial(state, side) < RULES.despairMaterial
  );
}

/**
 * The two numbers `destitute` is a comparison between.
 *
 * Exported so the banner can say *what would clear it* — an army told only
 * that it is doomed has been given a countdown rather than a way out, and the
 * way out here is concrete and reachable: another real piece, or ten units of
 * metal on ground you hold. Read from the same two functions the diagnosis
 * uses, so the advice on screen and the test that lifts it cannot drift.
 */
export function holdings(
  state: GameState,
  side: Side
): { pieces: number; material: number } {
  return {
    pieces: fightingPieces(state, side),
    material: workableMaterial(state, side)
  };
}

/**
 * Pieces that are not pawns, the king included.
 *
 * The king counts because it is three machines — an oven, a foundry and a
 * miner — so an army that still has one has a factory; and because "a lone
 * king" is the shape this mechanic exists for, which is one piece and not
 * zero. Pawns are excluded because two iron buys another, so counting them
 * would let a cornered army mint its way out of the diagnosis instead of out
 * of the position.
 */
function fightingPieces(state: GameState, side: Side): number {
  let count = 0;
  for (const [, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    if (pieceType(piece) !== PAWN) count++;
  }
  return count;
}

/**
 * Everything this side could still turn into a piece.
 *
 * Four places material lives and all four count: in its pieces' hands and
 * ovens, on its own lanes, lying loose on the floor, and — the one that
 * matters most — **unmined scrap on ground it holds**. A heap is not owned by
 * anybody; it is a corpse that any harvester can walk onto, which is what
 * makes "a lone king beside a graveyard" a factory rather than a hopeless
 * position.
 *
 * **"On ground it holds" is the correction, and it is the whole reason this
 * mechanic looked switched off.** Scrap on the floor used to be counted
 * wherever it lay, generously and on purpose: the two mistakes here are not
 * the same size, since too much hope costs a few turns of shuffling and too
 * little ends somebody's run. What that argument missed is that the generous
 * reading is not merely lenient, it is *unconditional* — after a war there is
 * always scrap somewhere, so the gate never closed at all and the question was
 * never asked. Reported from play as a lone king facing a full army with
 * nothing said and nothing happening.
 *
 * The rule is `holdsGround`, the same one belt placement uses: closer to this
 * side than to anybody else, ties to it. That keeps the fact a *fact* — it is
 * about distance on the board, not about whether a route is safe — and leaves
 * "could it get there and survive" where it belongs, in the simulation
 * underneath. It is still generous: ground the army merely happens to be
 * nearest to counts whether or not it dares walk onto it.
 */
function workableMaterial(state: GameState, side: Side): number {
  const industry = state.industry;
  const position = state.position;
  const ours = (sq: number): boolean => holdsGround(position, industry, sq, side);
  let total = 0;

  // Scrap on the floor. Grade 0, one unit each, owned by nobody — but a heap
  // in the middle of the army that made it is not this side's to dig.
  for (const sq of mineableSquares(industry)) {
    if (ours(sq)) total += mineableAt(industry, sq);
  }

  // Loose units, which a heap is not: these carry a grade.
  for (const sq of liveSquares(industry.dropped)) {
    if (ours(sq)) total += droppedAt(industry, sq).length;
  }

  // What its own pieces are holding, in both banks — a refinery's oven is
  // material that has been paid for and is one turn from being spendable.
  for (const [sq, piece] of state.position.board) {
    if (pieceColor(piece) !== side) continue;
    for (const store of ["in", "out"] as const) total += unitCount(industry, sq, store);
  }

  // And what is riding its own lanes.
  for (const sq of liveSquares(industry.beltCargo)) {
    if (beltOwnerAt(industry, sq) !== side) continue;
    total += cargoTotal(industry, sq);
  }

  // And what its own buildings hold (§26 A1). An army whose metal sits in
  // its altar or its warehouse is not hopeless — the altar hands its stock
  // on and a store's taps can be opened, so this is material one arrangement
  // away from being spendable, exactly as an oven's is.
  for (const sq of structureSquares(industry)) {
    const structure = structureAt(industry, sq)!;
    if (structure.owner !== side) continue;
    total += structure.stock.length;
  }

  return total;
}

/**
 * Play `side` forward against opponents that never move, and say whether it
 * ever delivers a win.
 *
 * The clock is **forced back** after each of its turns rather than the
 * opponents being removed from the board. Removing them would change the
 * question — an army with nothing to mate is not an army that cannot mate —
 * and it would delete the very kings this is asking about reaching.
 *
 * Everything goes through `applyCommand` on a cloned state, so the industrial
 * half ticks exactly as it would in a real game. That is not incidental: the
 * cheapest route out of depression is to mine a heap, cook it and forge a
 * rook, and a simulation that skipped the tick would report every factory in
 * the game as hopeless.
 *
 * `planOnce` rather than `planTurn`: this runs once a turn and sixty-four
 * deep, and the question is "is there *a* way", not "what is the best way".
 * The full planner's stances and rollouts buy accuracy that would only make
 * a hopeless position take longer to be declared hopeless.
 */
function canWinAlone(
  state: GameState,
  side: Side,
  level: Level,
  horizon: number
): boolean {
  const judge = skillFor(playingAt(level));
  let now = undraw(state);

  for (let turn = 0; turn < horizon; turn++) {
    // Somebody else has already been beaten — by us, since nobody else is
    // moving. A side that cannot move has been beaten in this game whether or
    // not its king is attacked, which is the test a stage boundary uses.
    if (isGameOver(now.status)) return won(now, side);

    // Hand the clock back. `planOnce` plans for whoever is on it, and the
    // opponents are meant to be statues.
    if (now.position.turn !== side) {
      now = { ...now, position: { ...now.position, turn: side as Color } };
    }
    if (legalMoves(now.position).length === 0) return false;

    let moved = false;
    for (const command of planOnce(now, side as Color, judge)) {
      try {
        now = undraw(applyCommand(now, command));
        moved = true;
      } catch {
        // A refused command is the planner's business. What matters here is
        // only whether the position ever becomes a win.
      }
    }
    // Nothing it could do with the turn at all. Not a win, and not going to
    // become one by being asked sixty more times.
    if (!moved) return false;
    // **End the turn** (§31 T2): the clock holds after the last move, and a
    // mate is settled at the handoff.
    if (now.position.turn === side && !isGameOver(now.status)) {
      try {
        now = undraw(applyCommand(now, { type: "pass" }));
      } catch {
        // In check after its own plan: not a turn that can end, so not a win.
        return false;
      }
    }
  }

  return isGameOver(now.status) && won(now, side);
}

/** The three chess draws. Mate and stalemate are verdicts; these are clocks. */
const DRAWN: ReadonlySet<GameStatus> = new Set<GameStatus>([
  "draw-material",
  "draw-fifty-move",
  "draw-repetition"
]);

/**
 * Suppress the three draws inside the simulation, because here they are
 * artefacts of the *method* rather than facts about the position.
 *
 * The opponents are frozen, so every position the mover returns to is an exact
 * repeat — threefold is not a sign that nothing is happening, it is guaranteed
 * for any army with few enough pieces to retrace its steps. Measured: a lone
 * king ran seventeen turns of its sixty-four and then the simulation declared
 * a repetition draw, so the whole industrial half of the horizon — the part
 * that mines, cooks and forges — was never reached. Fifty moves is the same
 * story (an industrial phase *is* fifty quiet moves) and insufficient material
 * is the very question being asked, since this is a game where a lone king can
 * build a rook.
 *
 * This is not a new rule: it is the one `gameStatus(..., !state.run)` already
 * applies to a run, for word-for-word the same reason, applied to a rollout
 * that has an even stronger claim on it. A drawn game also refuses commands,
 * so leaving the status set would stop the simulation dead rather than merely
 * end it early.
 */
function undraw(state: GameState): GameState {
  if (!DRAWN.has(state.status)) return state;
  return {
    ...state,
    status: "playing",
    // Both clocks, or the same draw fires again on the next command. The
    // simulated state is a throwaway clone; nothing outside sees either.
    repetitions: new Map(),
    position: { ...state.position, halfmove: 0 }
  };
}

/** Did `side` win the finished game in `state`? */
function won(state: GameState, side: Side): boolean {
  if (state.status !== "checkmate" && state.status !== "stalemate") return false;
  // The side on the clock is the one that has been beaten.
  return areEnemies(state.position, side, state.position.turn);
}

/**
 * The skill the simulation plays at, whatever the game is being played at.
 *
 * Deliberately **not** the difficulty in front of the player. A beginner's
 * opponent plays a share of its turns without looking at the reply at all, so
 * at `easy` the answer to "could this be won" would partly be "did the coin
 * come up right" — and an army would lay down its arms because the simulation
 * blundered on its behalf. The question is about the *position*, so it is
 * asked of somebody who is trying.
 *
 * The argument is taken and ignored rather than dropped from the signature: it
 * documents that the choice was made rather than forgotten, and the day a
 * profile wants to ask this at its own strength there is somewhere to put it.
 */
function playingAt(level: Level): Level {
  void level;
  return "hard";
}
